// utils/SoundManager.js
import { HowlCache } from "./State/Slices/soundsSlice"; // ← use the Redux cache
import { Howler } from "howler";
import { SOUND_NAME, SOUND_VOLUMES } from "./sounds";
import StorageManager from "./utils/StorageManager";

class SoundManager {
  constructor() {
    // Load saved settings from localStorage
    const savedSettings = StorageManager.loadSoundSettings();

    // Initialize with saved values or defaults
    this.soundEffectsEnabled = savedSettings.soundEffectsEnabled;
    this.backgroundMusicEnabled = savedSettings.backgroundMusicEnabled;
    this.globalSoundDisabled = savedSettings.globalSoundDisabled;
    this.masterVolume = savedSettings.masterVolume;
    Howler.volume(this.masterVolume);
    // Restore last active music track — falls back to base game music
    this.currentBackgroundMusic = savedSettings.currentBackgroundMusic || SOUND_NAME.background_music;
    this._activeSpriteIds = {};
    // When true, ambient music is playing instead of background music
    this.isAmbientActive = false;
    // When true, music toggle saves the setting but doesn't start playback.
    // releaseMusicPlayback() starts playback when intro popup closes.
    this.musicPlaybackDeferred = true;
  }

  /**
   * Call when intro popup closes — starts background music if enabled.
   * Determines the correct track from game state (not localStorage) to avoid stale values.
   */
  releaseMusicPlayback() {
    this.musicPlaybackDeferred = false;

    // Determine the correct music from current game state.
    // localStorage can be stale (e.g., bonus ended but localStorage wasn't cleared yet).
    try {
      const { default: store } = require('./State/Store/store');
      const state = store.getState();
      const inFeature = state.potCollection?.potCollectionActive === true;
      const correctMusic = inFeature
        ? SOUND_NAME.feature_background_music
        : SOUND_NAME.background_music;
      this._setCurrentBackgroundMusic(correctMusic);
    } catch (e) {
      // Store not ready — fall through to localStorage value
    }

    if (this.backgroundMusicEnabled && !this.globalSoundDisabled) {
      this.play(this.currentBackgroundMusic);
    }
  }

  /**
   * Get the configured volume for a sound (from SOUND_VOLUMES map).
   * Falls back to 1.0 if not listed.
   * @private
   */
  _getSoundVolume(name) {
    return SOUND_VOLUMES[name] ?? 1.0;
  }

  /**
   * Play a sound from the shared HowlCache
   * Handles both individual sounds and audio sprite sounds
   */
  play(name) {
    if (this.globalSoundDisabled) {
      // Still track which music SHOULD be playing so re-enabling picks the right track
      if (this._isBackgroundMusic(name)) {
        this._setCurrentBackgroundMusic(name);
      }
      return;
    }

    // Check if this sound is in the sprite
    const spriteSound = HowlCache['sfx_sprite'];
    if (spriteSound && spriteSound._sprite && spriteSound._sprite[name]) {
      // It's a sprite sound - play the specific sprite
      if (this.soundEffectsEnabled) {
        const id = spriteSound.play(name);
        // Apply per-sound volume to this sprite playback instance
        const vol = this._getSoundVolume(name);
        if (vol < 1.0) {
          spriteSound.volume(vol, id);
        }
        this._activeSpriteIds[name] = id;
      }
      return;
    }

    // Not a sprite sound - play as individual sound (looping sounds)
    const sound = HowlCache[name];
    if (!sound) {
      return;
    }

    // Apply per-sound volume
    const vol = this._getSoundVolume(name);
    sound.volume(vol);

    if (this._isMusic(name)) {
      // Track which background music should be active (even if muted),
      // so enabling music later plays the correct track for the current scene.
      // Guard: skip during startup (musicPlaybackDeferred) to avoid the base game
      // scene init overwriting the saved bonus track before releaseMusicPlayback fires.
      if (this._isBackgroundMusic(name) && !this.musicPlaybackDeferred) {
        this._setCurrentBackgroundMusic(name);
      }

      if (this.backgroundMusicEnabled) {
        sound.mute(false);
        if (!sound.playing()) {
          sound.play();
        }

        // GDD: start paired ambient track silently (vol 0) so both play in sync.
        // Crossfade only adjusts volumes — never starts/stops tracks.
        if (this._isBackgroundMusic(name)) {
          this._startAmbientSilently(name);
        }
      }
    } else if (this.soundEffectsEnabled) {
      sound.mute(false);
      sound.play();
    }
  }

  stop(name) {
    // Check sprite sounds first
    const spriteSound = HowlCache['sfx_sprite'];
    if (spriteSound && spriteSound._sprite && spriteSound._sprite[name]) {
      const id = this._activeSpriteIds[name];
      if (id !== undefined) {
        spriteSound.stop(id);
        delete this._activeSpriteIds[name];
      }
      return;
    }

    const sound = HowlCache[name];
    if (sound && sound.playing()) {
      sound.stop();

      // If stopping background/feature music, also stop its paired ambient
      if (this._isBackgroundMusic(name)) {
        this.isAmbientActive = false;
        const ambientName = this._getAmbientFor(name);
        if (ambientName) {
          const ambientMusic = HowlCache[ambientName];
          if (ambientMusic && ambientMusic.playing()) {
            ambientMusic.stop();
          }
        }
      }
    }
  }

  /**
   * Fade out a sound gradually over the specified duration
   * @param {string} name - Sound name to fade out
   * @param {number} duration - Duration in milliseconds (default: 1000ms)
   * @param {Function} onComplete - Optional callback when fade completes
   */
  fadeOut(name, duration = 1000, onComplete = null) {
    const sound = HowlCache[name];
    if (!sound) {
      if (onComplete) onComplete();
      return;
    }

    if (!sound.playing()) {
      if (onComplete) onComplete();
      return;
    }

    const currentVolume = sound.volume();
    sound.fade(currentVolume, 0, duration);

    // If fading out background/feature music, also stop any playing ambient
    if (this._isBackgroundMusic(name)) {
      this.isAmbientActive = false;
      const ambientName = this._getAmbientFor(name);
      if (ambientName) {
        const ambientMusic = HowlCache[ambientName];
        if (ambientMusic && ambientMusic.playing()) {
          const ambientVolume = ambientMusic.volume();
          ambientMusic.fade(ambientVolume, 0, duration);
          setTimeout(() => {
            ambientMusic.stop();
            ambientMusic.volume(this._getSoundVolume(ambientName));
          }, duration);
        }
      }
    }

    // Stop the sound after fade completes
    setTimeout(() => {
      sound.stop();
      // Reset to master volume for next play
      sound.volume(this.masterVolume);
      if (onComplete) onComplete();
    }, duration);
  }

  /**
   * Fade in a sound gradually over the specified duration
   * @param {string} name - Sound name to fade in
   * @param {number} duration - Duration in milliseconds (default: 1000ms)
   * @param {number} targetVolume - Target volume (default: uses masterVolume)
   * @param {Function} onComplete - Optional callback when fade completes
   */
  fadeIn(name, duration = 1000, targetVolume = null, onComplete = null) {
    const sound = HowlCache[name];
    if (!sound) {
      if (onComplete) onComplete();
      return;
    }

    if (this.globalSoundDisabled) {
      // Still track which music SHOULD be playing so re-enabling picks the right track
      if (this._isBackgroundMusic(name)) {
        this._setCurrentBackgroundMusic(name);
      }
      if (onComplete) onComplete();
      return;
    }

    // Check if it's music and if music is enabled
    if (this._isMusic(name)) {
      if (!this.backgroundMusicEnabled) {
        // Still track which music SHOULD be playing
        if (this._isBackgroundMusic(name)) {
          this._setCurrentBackgroundMusic(name);
        }
        if (onComplete) onComplete();
        return;
      }
    } else {
      if (!this.soundEffectsEnabled) {
        if (onComplete) onComplete();
        return;
      }
    }

    // Use per-sound volume as default target (respects SOUND_VOLUMES config)
    const actualTargetVolume = targetVolume !== null ? targetVolume : this._getSoundVolume(name);

    // Auto-track which background music is active (skip ambient tracks)
    if (this._isBackgroundMusic(name)) {
      this._setCurrentBackgroundMusic(name);
    }

    // Set initial volume to 0 and start playing
    sound.volume(0);
    sound.mute(false);
    sound.play();

    // Fade in to target volume
    sound.fade(0, actualTargetVolume, duration);

    // Reset ambient state — background music is now active
    // Also restart ambient silently so both tracks play in sync
    if (this._isBackgroundMusic(name)) {
      this.isAmbientActive = false;
      this._startAmbientSilently(name);
    }

    // Call onComplete after fade completes
    if (onComplete) {
      setTimeout(() => {
        onComplete();
      }, duration);
    }
  }

  toggleGlobal(disabled) {
    this.globalSoundDisabled = disabled;
    Object.values(HowlCache).forEach((sound) => {
      sound.mute(disabled);
      if (disabled) sound.stop();
    });
    this.soundEffectsEnabled = !disabled;
    this.backgroundMusicEnabled = !disabled;

    // If re-enabling, start current background music + ambient at vol 0
    if (!disabled && this.backgroundMusicEnabled && !this.musicPlaybackDeferred) {
      this.isAmbientActive = false;
      const currentMusic = HowlCache[this.currentBackgroundMusic];
      if (currentMusic) {
        currentMusic.mute(false);
        if (!currentMusic.playing()) currentMusic.play();
      }
      this._startAmbientSilently(this.currentBackgroundMusic);
    }
  }

  toggleBackgroundMusic(enabled) {
    this.backgroundMusicEnabled = enabled;
    const current = HowlCache[this.currentBackgroundMusic];
    const ambientName = this._getAmbientFor(this.currentBackgroundMusic);
    const ambientMusic = ambientName ? HowlCache[ambientName] : null;

    if (enabled && !this.globalSoundDisabled && !this.musicPlaybackDeferred) {
      // Start background music + ambient at vol 0 (both play in sync)
      this.isAmbientActive = false;
      if (current) {
        current.mute(false);
        current.play();
      }
      this._startAmbientSilently(this.currentBackgroundMusic);
    } else {
      this.isAmbientActive = false;
      if (current) {
        current.stop();
      }
      // Also stop ambient
      if (ambientMusic && ambientMusic.playing()) {
        ambientMusic.stop();
      }
    }
  }

  /**
   * Switch to a different background music track
   * @param {string} musicName - The music track to switch to
   */
  switchBackgroundMusic(musicName) {
    const wasPlaying = this.backgroundMusicEnabled && !this.globalSoundDisabled;

    // Stop current music (background or ambient) if playing
    if (this.currentBackgroundMusic) {
      const currentMusic = HowlCache[this.currentBackgroundMusic];
      if (currentMusic && currentMusic.playing()) {
        currentMusic.stop();
      }
      // Always stop paired ambient too (it's always playing at vol 0 or higher)
      const ambientName = this._getAmbientFor(this.currentBackgroundMusic);
      if (ambientName) {
        const ambientMusic = HowlCache[ambientName];
        if (ambientMusic && ambientMusic.playing()) {
          ambientMusic.stop();
        }
      }
    }

    // Reset ambient state — new scene always starts with background music
    this.isAmbientActive = false;

    // Update current music reference and persist
    this._setCurrentBackgroundMusic(musicName);

    // Play new music + start paired ambient silently
    if (wasPlaying) {
      const newMusic = HowlCache[musicName];
      if (newMusic) {
        newMusic.mute(false);
        newMusic.play();
      }
      this._startAmbientSilently(musicName);
    }
  }

  setMasterVolume(volume) {
    this.masterVolume = Math.max(0, Math.min(1, volume));
    Howler.volume(this.masterVolume);
  }

  // ── Ambient / Background crossfade (inactivity-driven) ──────────────────
  // GDD approach: both tracks play simultaneously from the start.
  // Crossfade only adjusts volumes — tracks are never started/stopped here.

  /**
   * Crossfade from background music to its paired ambient music.
   * Called after a period of player inactivity.
   * Both tracks are already playing (ambient at vol 0). Just swap volumes.
   * @param {number} duration - Crossfade duration in ms (default: 2000)
   */
  crossfadeToAmbient(duration = 2000) {
    if (this.isAmbientActive) return;
    if (this.globalSoundDisabled || !this.backgroundMusicEnabled) return;
    if (this.musicPlaybackDeferred) return;

    const bgName = this.currentBackgroundMusic;
    const ambientName = this._getAmbientFor(bgName);
    if (!ambientName) return;

    const bgSound = HowlCache[bgName];
    const ambientSound = HowlCache[ambientName];
    if (!bgSound || !ambientSound) return;

    this.isAmbientActive = true;

    // Fade background volume down to 0
    if (bgSound.playing()) {
      bgSound.fade(bgSound.volume(), 0, duration);
    }

    // Fade ambient volume up to its target
    const targetVol = this._getSoundVolume(ambientName);
    if (ambientSound.playing()) {
      ambientSound.fade(ambientSound.volume(), targetVol, duration);
    }
  }

  /**
   * Crossfade from ambient music back to its paired background music.
   * Called when the player becomes active (e.g. starts a spin).
   * Both tracks are already playing. Just swap volumes.
   * @param {number} duration - Crossfade duration in ms (default: 2000)
   */
  crossfadeToBackground(duration = 2000) {
    if (!this.isAmbientActive) return;
    if (this.globalSoundDisabled || !this.backgroundMusicEnabled) return;

    const bgName = this.currentBackgroundMusic;
    const ambientName = this._getAmbientFor(bgName);
    if (!ambientName) return;

    const bgSound = HowlCache[bgName];
    const ambientSound = HowlCache[ambientName];
    if (!bgSound || !ambientSound) return;

    this.isAmbientActive = false;

    // Fade ambient volume down to 0
    if (ambientSound.playing()) {
      ambientSound.fade(ambientSound.volume(), 0, duration);
    }

    // Fade background volume up to its target
    const targetVol = this._getSoundVolume(bgName);
    if (bgSound.playing()) {
      bgSound.fade(bgSound.volume(), targetVol, duration);
    }
  }

  /**
   * Returns the ambient music track name that pairs with a given background music.
   * background_music        → ambient_music
   * feature_background_music → feature_ambient_music
   * @returns {string|null}
   */
  _getAmbientFor(name) {
    if (name === SOUND_NAME.background_music && SOUND_NAME.ambient_music) {
      return SOUND_NAME.ambient_music;
    }
    if (name === SOUND_NAME.feature_background_music && SOUND_NAME.feature_ambient_music) {
      return SOUND_NAME.feature_ambient_music;
    }
    return null;
  }

  /**
   * Update currentBackgroundMusic and persist to localStorage.
   * Called automatically by play() and fadeIn() for music tracks.
   */
  _setCurrentBackgroundMusic(name) {
    this.currentBackgroundMusic = name;
    StorageManager.saveSoundSettings({
      masterVolume: this.masterVolume,
      soundEffectsEnabled: this.soundEffectsEnabled,
      backgroundMusicEnabled: this.backgroundMusicEnabled,
      globalSoundDisabled: this.globalSoundDisabled,
      currentBackgroundMusic: name,
    });
  }

  /**
   * Check if a sound name is a background music track (not ambient).
   * These are the tracks that currentBackgroundMusic can be set to.
   */
  _isBackgroundMusic(name) {
    return [
      SOUND_NAME.background_music,
      SOUND_NAME.feature_background_music,
      SOUND_NAME.free_spin_background_music,
    ].includes(name);
  }

  _isMusic(name) {
    if (this._isBackgroundMusic(name)) return true;
    // Ambient tracks are also music
    if (SOUND_NAME.ambient_music && name === SOUND_NAME.ambient_music) return true;
    if (SOUND_NAME.feature_ambient_music && name === SOUND_NAME.feature_ambient_music) return true;
    return false;
  }

  /**
   * Start the paired ambient track silently (volume 0) alongside its background music.
   * Both tracks play in sync from the start — crossfade only adjusts volumes.
   * @param {string} bgName - The background music track name
   * @private
   */
  _startAmbientSilently(bgName) {
    const ambientName = this._getAmbientFor(bgName);
    if (!ambientName) return;

    const ambientSound = HowlCache[ambientName];
    if (!ambientSound) return;

    // Start at volume 0 so it plays in sync but is inaudible
    ambientSound.volume(0);
    ambientSound.mute(false);
    if (!ambientSound.playing()) {
      ambientSound.play();
    }
  }

}

export const soundManager = new SoundManager();
